import { html } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { classMap } from 'lit-html/directives/class-map.js';
import { KatalComponent, Keys, KatLitElement } from '../../shared/base';
import {
  numberOfDaysInMonth,
  addDay,
  daysBeforeFirstOfMonth,
  isLeapYear,
  dateCopy,
  isSameMonth,
  applyDay,
} from '../../shared/base/date-utils';
import { coerceToDate } from '../../shared/base/type-utils';
import {
  daysOfWeekByLocale,
  monthsOfYearByLocale,
  fullDaysOfMonth,
} from './date-locale-cache';

const DATE_NOT_SELECTED = -999;
const DAYS_IN_A_WEEK = 7;
const MAX_DAYS_TO_DISPLAY = DAYS_IN_A_WEEK * 6;

/**
 * @component {kat-calendar} KatalCalendar Calendar component
 * @example USCalendar {"locale": "en-US"}
 * @event change A change event will fire when a date has been selected
 * @event hide A change event will fire when a date has been selected
 * @status Production
 * @theme flo
 */
class KatalCalendar extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string} value This is the text that will be displayed to the user
       * @required
       */
      value: { type: String },

      /**
       * @classprop {string} locale This is the locale the calendar should be using
       */
      locale: { type: String, reflect: true },

      /**
       * Used to define a function that is called each time a day is rendered.
       * @classprop {(date: Date) => boolean} isDateDisabled Function called each time a day is rendered. Passed the date the day represents.
       */
      isDateDisabled: { attribute: false },

      // Internal only
      _isValueSet: { attribute: false },
      _valueDate: { attribute: false },
      current: { attribute: false },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();

    this.locale = 'en-US';
    this._isValueSet = false;
    this._valueDate = new Date();
    this.current = dateCopy(this._valueDate);
    this.today = this._valueDate;

    this._daysShort = daysOfWeekByLocale(this.locale, 'short');
    this._daysLong = daysOfWeekByLocale(this.locale, 'long');
    this._monthsLong = monthsOfYearByLocale(this.locale, 'long');
    this._dayLabelsKey = null;
    this._dayLabels = null;
  }

  // todo: make this consistent with date-picker, which has a dateValue and value
  set value(value) {
    this._isValueSet = !!value;
    this._valueDate = coerceToDate(value);
    this.current = dateCopy(this._valueDate);
  }

  get value() {
    return this._valueDate;
  }

  getFormattedValue() {
    return !this._isValueSet ? '' : this.format(this.value);
  }

  format(date) {
    function pad(num) {
      return (num < 10 ? '0' : '') + num;
    }

    const delimeter = '/';
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return [pad(month), pad(day), year].join(delimeter);
  }

  emitEvent() {
    const event = {
      value: this.getFormattedValue(),
      date: this.value,
    };
    this.dispatchCustomEvent('change', event);
  }

  onClickDay(node) {
    const day = +node.textContent;
    const isFuture = node.classList.contains('future');
    const isPast = node.classList.contains('past');
    if (node.classList.contains('disabled')) {
      return;
    }

    if (isFuture) {
      this.navigateMonths(1);
      return;
    }
    if (isPast) {
      this.navigateMonths(-1);
      return;
    }

    this.value = applyDay(this.current, day ? day : 1);
    this.emitEvent();
  }

  navigateMonths(direction) {
    const date = applyDay(this.current, 1);
    date.setMonth(date.getMonth() + direction);
    date.setDate(Math.min(this.current.getDate(), numberOfDaysInMonth(date)));
    this.current = date;
  }

  render() {
    const month = this.current.getMonth();
    const year = this.current.getFullYear();
    const prevMonthLabel = this._getMonthLabel(month - 1, year);
    const currentMonth = this._getMonthLabel(month, year);
    const nextMonthLabel = this._getMonthLabel(month + 1, year);

    return html`
      <div class="calendar" @focusout="${this._onFocusOut}">
        <div class="cal-header">
          <button
            class="cal-lft kat-no-style"
            aria-label="${prevMonthLabel}"
            @click="${this._calendarDirectionClick}"
            @keydown="${this._monthChangeKeyHandler}"
          >
            <kat-icon name="chevron-left" size="small"></kat-icon>
          </button>
          <span class="cal-month" role="presentation">
            ${currentMonth}
          </span>
          <button
            class="cal-rgt kat-no-style"
            aria-label="${nextMonthLabel}"
            @click="${this._calendarDirectionClick}"
            @keydown="${this._monthChangeKeyHandler}"
          >
            <kat-icon name="chevron-right" size="small"></kat-icon>
          </span>
        </div>
        <div
          class="cal-container"
          @mousedown="${this._mouseDownEventHandler}"
          @click="${this._clickEventHandler}"
          @keydown="${this._keyChangeEventHandler}"
        >
          <table class="cal-body">
            <thead><tr>
            ${this._daysShort.map((dayofWeek, i) => {
              return html`
                <th class="day-of-week" aria-label="${this._daysLong[i]}">
                  ${dayofWeek}
                </th>
              `;
            })}
            </tr></thead>
            <tbody>
            ${this._renderDays(prevMonthLabel, nextMonthLabel)}
            </tbody>
          </div>
        </div>
      </div>
    `;
  }

  focusDay() {
    const button = this.querySelector(
      `.day.on button[data-day='${this.current.getDate()}']`
    );
    if (button) {
      button.focus();
    }
  }

  firstUpdated() {
    const calendar = this.querySelector('.calendar');
    const events = ['keyup', 'keydown', 'keypressed', 'click'];
    events.forEach(key =>
      calendar.addEventListener(key, e => e.stopImmediatePropagation())
    );
  }

  shouldUpdate(changedProps) {
    if (changedProps.has('locale')) {
      this._daysShort = daysOfWeekByLocale(this.locale, 'short');
      this._daysLong = daysOfWeekByLocale(this.locale, 'long');
      this._monthsLong = monthsOfYearByLocale(this.locale, 'long');
    }

    return true;
  }

  updated() {
    if (this.updateFocus) {
      this.updateFocus = false;
      this.focusDay();
    }
  }

  _renderDays(prevMonth, nextMonth) {
    const dayLabels = this._memoizeDayLabels();
    const weeks = [];
    let weekDay = 0;

    const days = [];
    const daysBeforeFirst = daysBeforeFirstOfMonth(this.current);
    const numberOfDays = numberOfDaysInMonth(this.current);
    const daysAfterLast = MAX_DAYS_TO_DISPLAY - numberOfDays - daysBeforeFirst;
    const dateToday = this.getSelectedDate(this.today, this.current);
    const dateSelected = this._isValueSet
      ? this.getSelectedDate(this.value, this.current)
      : DATE_NOT_SELECTED;
    const dateHighlighted = this.current.getDate();

    function handleWeekDay() {
      weekDay++;
      if (weekDay > 6) {
        weekDay = 0;
        weeks.push(
          html`
            <tr>
              ${days.splice(0)}
            </tr>
          `
        );
      }
    }

    for (let i = 1; i <= daysBeforeFirst; i++) {
      days.push(
        html`
          <td class="day off past">
            <button aria-label="${prevMonth}" tabindex="-1"></button>
          </td>
        `
      );
      handleWeekDay();
    }
    for (let dayOfMonth = 1; dayOfMonth <= numberOfDays; dayOfMonth++) {
      const tabIndex = dayOfMonth === dateHighlighted ? 0 : -1;

      const classes = { day: true, on: true };
      classes.today = dayOfMonth === dateToday;
      classes.selected = dayOfMonth === dateSelected;
      classes['last-day'] = dayOfMonth === numberOfDays;
      classes.disabled = false;
      if (this.isDateDisabled) {
        const date = applyDay(this.current, dayOfMonth);
        classes.disabled = this.isDateDisabled(date);
      }

      const buttonClasses = {
        'kat-no-style': true,
        highlighted: dayOfMonth === dateHighlighted,
      };

      const ariaToday = classes.today ? 'date' : undefined;

      days.push(
        html`
          <td class=${classMap(classes)}>
            <button
              aria-disabled="${classes.disabled}"
              tabindex="${tabIndex}"
              aria-label="${dayLabels[dayOfMonth - 1]}"
              aria-current=${ifDefined(ariaToday)}
              aria-pressed="${classes.selected}"
              data-day="${dayOfMonth}"
              class=${classMap(buttonClasses)}
            >
              ${dayOfMonth}
            </button>
          </td>
        `
      );

      handleWeekDay();
    }

    for (let i = 1; i <= daysAfterLast; i++) {
      days.push(
        html`
          <td class="day off future">
            <button aria-label="${nextMonth}" tabindex="-1"></button>
          </td>
        `
      );
      handleWeekDay();
    }

    return weeks;
  }

  _calendarDirectionClick(e) {
    e.stopPropagation();
    const direction = e.currentTarget.classList.contains('cal-rgt') ? 1 : -1;
    this.navigateMonths(direction);
  }

  _mouseDownEventHandler(e) {
    const node = e.target.closest('.day');
    if (!node) return;

    const isDisabled = node.classList.contains('disabled');
    if (isDisabled) {
      e.preventDefault();
    }
  }

  _clickEventHandler(e) {
    e.stopImmediatePropagation();
    this.dispatchCustomEvent('pre-click', e);
    const node = e.target.closest('.day');
    if (node) {
      this.onClickDay(node);
    }
  }

  _keyChangeEventHandler(e) {
    let days;
    switch (e.keyCode) {
      case Keys.ArrowLeft:
        days = -1;
        break;
      case Keys.ArrowRight:
        days = 1;
        break;
      case Keys.ArrowUp:
        days = -7;
        break;
      case Keys.ArrowDown:
        days = 7;
        break;
      case Keys.PageUp:
        days = numberOfDaysInMonth(this.current);
        break;
      case Keys.PageDown: {
        const date = dateCopy(this.current);
        date.setMonth(date.getMonth() - 1);
        days = -numberOfDaysInMonth(date);
        break;
      }
      case Keys.Home:
        days = isLeapYear(this.current) ? 366 : 365;
        break;
      case Keys.End:
        days = isLeapYear(this.current) ? -366 : -365;
        break;
      case Keys.Escape:
        this.dispatchCustomEvent('hide', {}, false);
        return false;
    }

    if (days) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.current = addDay(this.current, days);
      this.updateFocus = true;
      return false;
    }
  }

  _monthChangeKeyHandler(e) {
    if (e.keyCode === Keys.Escape) {
      this.dispatchCustomEvent('hide', {}, false);
      return false;
    }
  }

  _onFocusOut(evt) {
    evt.stopImmediatePropagation();
    const relatedTarget = evt.relatedTarget;
    const isOffDay = !!evt.target.closest('.day.off');

    if (!this.contains(relatedTarget) && !isOffDay && !this.updateFocus) {
      this.dispatchCustomEvent('fullblur');
    }
  }

  /**
   * Returns a label to describe a month such as "February 2020" given a month index and year number.
   * Adjusts month and year if month specified is outside of [0 ... 11] but doesn't handle if the month is outside of [-11 ... 23] (more than one year distant).
   */
  _getMonthLabel(month, fullYear) {
    if (month < 0) {
      month += 12;
      fullYear--;
    }
    if (month > 11) {
      month -= 12;
      fullYear++;
    }
    return this._monthsLong[month] + ' ' + fullYear;
  }

  _memoizeDayLabels() {
    const key =
      this.current.getMonth() +
      '-' +
      this.current.getFullYear() +
      '-' +
      this.locale;
    if (this._dayLabelsKey !== key) {
      this._dayLabels = fullDaysOfMonth(this.current, this.locale);
      this._dayLabelsKey = key;
    }
    return this._dayLabels;
  }

  getSelectedDate(date, current) {
    if (isSameMonth(date, current)) {
      return date.getDate();
    }
    return DATE_NOT_SELECTED;
  }
}

KatalComponent.register('kat-calendar', KatalCalendar);
